import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import * as _ from 'lodash';
import { BehaviorSubject, combineLatest, of, Subscription } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';

import { AutoUnsubscribe, getSupportEmail } from 'helpers';
import { SegmentService } from 'shared/services';
import { AppUser, AuthenticationModel } from '../../../authentication/authentication.model';
import { SignupErrorType } from '../types';
import { Invitation, InvitationService } from './invitation.service';

@Component({
    selector: 'fc-signup-invite',
    templateUrl: './invite.component.html',
    standalone: false,
    providers: [AuthenticationModel, InvitationService]
})
@AutoUnsubscribe
export class SignupInviteComponent implements OnInit {
    error$ = new BehaviorSubject<SignupErrorType | null>(null);
    redirectToLoginAfterError$ = new BehaviorSubject<boolean>(false);

    supportEmail = getSupportEmail();

    private _subscription$ = new Subscription();

    constructor(
        private authService: AuthService,
        private invitationService: InvitationService,
        private authenticationModel: AuthenticationModel,
        private segmentService: SegmentService,
        private route: ActivatedRoute
    ) {}

    ngOnInit(): void {
        const invitationToken = this.route.snapshot.paramMap.get('token');

        const invitation$ = combineLatest([
            this.invitationService.getInvitation(invitationToken),
            this.authService.isAuthenticated$
        ]).pipe(
            switchMap(([invitation, isAuthenticated]) => {
                if (!isAuthenticated) {
                    return this.redirectToLogin(invitation).pipe(map(() => null));
                }
                return of<Invitation>(invitation);
            })
        );

        const appUser$ = this.authService.user$.pipe(
            filter((user) => !!user),
            switchMap((authenticatedUser) => this.authenticationModel.getAppUser(authenticatedUser.sub))
        );

        this._subscription$.add(
            combineLatest([invitation$, appUser$])
                .pipe(
                    switchMap(([invitation, appUser]) => {
                        return this.finishSignup({ appUser, invitation }).pipe(
                            switchMap(() => {
                                return this.authService.loginWithRedirect({
                                    appState: { target: '/' },
                                    authorizationParams: {
                                        screen_hint: 'login',
                                        ...(invitation?.inviteeEmail && { login_hint: invitation?.inviteeEmail }),
                                        ...(invitation?.organization?._id && {
                                            organization: invitation.organization?._id.toString()
                                        })
                                    }
                                });
                            })
                        );
                    }),
                    catchError((error) => {
                        const errorType = _.get(
                            typeof error === 'function' ? error() : error,
                            'data.type',
                            SignupErrorType.UnknownError
                        );
                        this.error$.next(errorType);
                        this.redirectToLoginAfterError$.next(errorType === SignupErrorType.InvitationUsedError);
                        return of(null);
                    })
                )
                .subscribe()
        );
    }

    retry() {
        this.error$.next(null);
        this.redirectToLoginAfterError$.next(false);
        window.location.reload();
    }

    private redirectToLogin(invitation: Invitation) {
        const screenHint = this.route.snapshot.queryParamMap.get('screen_hint') || 'signup';
        return this.authService.loginWithRedirect({
            appState: {
                target: `/signup/invite/${invitation.token}`
            },
            authorizationParams: {
                screen_hint: screenHint,
                ...(invitation?.inviteeEmail && { login_hint: invitation?.inviteeEmail })
            }
        });
    }

    private finishSignup({ appUser, invitation }: { appUser: AppUser; invitation: Invitation }) {
        const signupData = {
            token: invitation.token,
            termsAccepted: appUser.appMetadata.termsConditionsConsent,
            marketingEmailConsentAccepted: appUser.appMetadata.marketingEmailConsent ?? false,
            user: {
                firstname: appUser.givenName,
                surname: appUser.familyName
            }
        };

        return this.invitationService.signup(signupData).pipe(
            tap(() => {
                this.segmentService.track('New user invited signup', {
                    firstname: invitation.inviteeEmail,
                    surname: invitation.inviteeEmail,
                    organizationType: invitation.organization?.type
                });
            }),
            catchError((error) => {
                this.segmentService.track('New user invited signup failed', {
                    firstname: invitation.inviteeEmail,
                    surname: invitation.inviteeEmail,
                    organizationType: invitation.organization?.type
                });

                throw error;
            })
        );
    }
}
